import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Check, X, Loader2 } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { fmtDob, fmtSex, titleCaseName } from "@/lib/patientFormat";
import { getProviderConfig, toLocalInput } from "@/lib/scheduler";
import { listAppointments } from "@/lib/telehealth";
import { SlotGrid } from "@/components/telehealth/SlotGrid";

function defaultWhen() {
  const d = new Date(Date.now() + 60 * 60 * 1000); // +1h
  d.setMinutes(0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Right-side "Book in Echo" dock — mirrors the Echo scheduler format (a light record
// surface that slides in from the right). Booking is real: POST /televisit/appointments.
export default function ScheduleModal({ open, onOpenChange, patient, clientId }) {
  const [providers, setProviders] = useState([]);
  const [npi, setNpi] = useState("");
  const [visitTypes, setVisitTypes] = useState([]);
  const [visitTypeId, setVisitTypeId] = useState("");
  const [when, setWhen] = useState(defaultWhen());
  const [duration, setDuration] = useState("30");
  const [reason, setReason] = useState("Telemedicine visit");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [config, setConfig] = useState(null);
  const [appts, setAppts] = useState([]);
  const [date, setDate] = useState(() => toLocalInput(new Date()).slice(0, 10));
  const [slotStart, setSlotStart] = useState(null);

  const configured = useMemo(
    () => !!config && Object.values(config.weekly || {}).some((w) => (w || []).length),
    [config],
  );

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setCopied(false);
    setSlotStart(null);
    setConfig(null);
    (async () => {
      try {
        const { data } = await api.get("/api/edi/note-to-claim/providers");
        const list = (data?.providers || []).filter((p) => String(p.client_id) === String(clientId));
        setProviders(list);
        if (list[0]) setNpi(list[0].npi_number);
      } catch {
        setProviders([]);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get(`/api/edi/televisit/visit-types?client_id=${encodeURIComponent(clientId)}`);
        setVisitTypes(data?.visit_types || []);
      } catch {
        setVisitTypes([]);
      }
    })();
  }, [open, clientId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) onOpenChange(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  // Provider availability config + their booked appointments (for slot conflicts).
  useEffect(() => {
    if (!open || !npi) { setConfig(null); return undefined; }
    let alive = true;
    setSlotStart(null);
    getProviderConfig(clientId, npi).then((c) => { if (alive) setConfig(c); }).catch(() => { if (alive) setConfig(null); });
    listAppointments(clientId).then((a) => { if (alive) setAppts(a); }).catch(() => { if (alive) setAppts([]); });
    return () => { alive = false; };
  }, [open, npi, clientId]);

  const pickSlot = (d) => {
    setSlotStart(d);
    setWhen(toLocalInput(d));
    if (config?.slot_minutes) setDuration(String(config.slot_minutes));
  };

  const selectedProvider = useMemo(
    () => providers.find((p) => p.npi_number === npi),
    [providers, npi],
  );

  const inviteUrl = useMemo(() => {
    const path = result?.patient_link_path || result?.appointment?.patient_link_path;
    if (!path) return "";
    return path.startsWith("http") ? path : `https://ehi.ariaone.ai${path}`;
  }, [result]);

  const submit = async () => {
    if (!npi) { toast.error("Select a rendering provider NPI"); return; }
    if (!when) { toast.error(configured ? "Pick a time slot" : "Pick a date & time"); return; }
    setSubmitting(true);
    try {
      const primaryTax = (selectedProvider?.taxonomies || []).find((t) => t.primary) || (selectedProvider?.taxonomies || [])[0];
      const vt = visitTypes.find((v) => String(v.visit_type_id ?? v.id) === String(visitTypeId));
      const payload = {
        client_id: String(clientId),
        npi,
        patient_name: patient.full_name || `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
        scheduled_at: new Date(when).toISOString(),
        duration_minutes: Number(duration) || 30,
        reason: reason || "Telemedicine visit",
        provider_name: selectedProvider?.provider_name,
        taxonomy_code: primaryTax?.code,
        // Chart linkage — so the booked visit deep-links to the patient's chart
        // and the live-visit snapshot can load. Resolved from patient search.
        patient_mrn: patient.mrn || undefined,
        patient_chart_uuid: patient.chart_uuid || undefined,
        patient_chart_id: patient.chart_id || undefined,
        patient_dob: patient.dob || undefined,
        patient_gender: patient.gender || undefined,
        patient_phone: patient.cell || patient.home_phone || undefined,
        patient_email: patient.email || undefined,
        ...(vt ? { visit_type_id: vt.visit_type_id ?? vt.id, visit_type_name: vt.name ?? vt.visit_type_name } : {}),
      };
      const { data } = await api.post("/api/edi/televisit/appointments", payload);
      setResult(data);
      toast.success("Booked in Echo");
    } catch (e) {
      if (e?.response?.status === 409) toast.error("That slot is already booked (double-booking). Pick another time.");
      else toast.error(describeError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteUrl); setCopied(true); setTimeout(() => setCopied(false), 1600); }
    catch { toast.error("Could not copy"); }
  };

  if (!patient) return null;

  const patientName = titleCaseName(patient.full_name || `${patient.first_name || ""} ${patient.last_name || ""}`.trim());

  return (
    <>
      {/* scrim */}
      <div
        onClick={() => onOpenChange(false)}
        aria-hidden
        className={`fixed inset-0 z-[110] transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        style={{ background: "rgba(15,23,42,.42)", backdropFilter: "blur(4px)" }}
      />
      {/* dock */}
      <aside
        role="dialog"
        aria-label="Book appointment in Echo"
        data-testid="schedule-modal"
        className={`fixed top-0 right-0 bottom-0 z-[111] w-full sm:w-[480px] flex flex-col bg-white border-l border-black/[.08] transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        style={{ boxShadow: "-20px 0 48px rgba(15,23,42,.13)", fontFamily: "'Inter',system-ui,sans-serif" }}
      >
        {/* header */}
        <div className="flex items-center gap-2.5 px-[18px] py-[15px] border-b border-black/[.08]">
          <span className="w-6 h-6 rounded-[6px] bg-[#0F172A] grid place-items-center overflow-hidden">
            <img src="/echo-emblem-white.webp" alt="Echo" className="w-[15px] h-[15px] object-contain" />
          </span>
          <b className="text-[15px] font-semibold tracking-[-.01em] text-[#0F172A]">Book appointment</b>
          <span className="ml-auto text-[11px] text-[#8A8F99]">Echo · opened from revQ</span>
          <button onClick={() => onOpenChange(false)} data-testid="schedule-close"
            className="w-7 h-7 rounded-[7px] border border-black/[.1] text-[#3F4551] grid place-items-center hover:bg-black/[.04]">
            <X size={14} />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto px-[18px] py-4 flex flex-col gap-4">
          {/* patient chip */}
          <div className="flex items-center gap-2 rounded-[10px] border border-black/[.08] bg-[#F6F6F5] px-3 py-2.5">
            <div className="min-w-0">
              <div className="font-semibold text-[13.5px] text-[#0F172A] truncate">{patientName}</div>
              <div className="text-[11.5px] text-[#8A8F99] font-mono truncate">
                MRN {patient.mrn} · {fmtDob(patient.dob)} · {fmtSex(patient.gender)} · Client {clientId}
              </div>
            </div>
            <span className="ml-auto text-[10px] uppercase tracking-[.05em] text-[#8A8F99] border border-black/[.1] rounded-full px-2 py-0.5 whitespace-nowrap bg-white">
              prefilled · revQ patient row
            </span>
          </div>

          {result ? (
            <div className="flex flex-col gap-3.5" data-testid="schedule-result">
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-full border border-black/[.12] grid place-items-center text-[#0F172A]"><Check size={18} /></span>
                <div className="text-[16px] font-semibold text-[#0F172A]">Booked in Echo</div>
              </div>
              {inviteUrl ? (
                <div>
                  <Label className="text-[11px] text-[var(--gray2)]">Patient invite link</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input readOnly value={inviteUrl} className="text-[11px]" data-testid="schedule-invite-link" />
                    <button type="button" onClick={copyLink} data-testid="schedule-copy-link"
                      className="shrink-0 w-9 h-9 rounded-[9px] border border-black/[.1] grid place-items-center hover:bg-black/[.04] text-[#3F4551]">
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-[12px] text-[var(--gray2)]">Appointment created. No public invite link was returned.</div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3.5">
              <div>
                <Label className="text-[11px] text-[var(--gray2)]">Rendering provider (NPI)</Label>
                {providers.length === 0 ? (
                  <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 mt-1" data-testid="schedule-no-provider">
                    No provider NPI is linked to Client {clientId}. Link a provider to this client before booking.
                  </div>
                ) : (
                  <Select value={npi} onValueChange={setNpi}>
                    <SelectTrigger className="mt-1" data-testid="schedule-npi-select"><SelectValue placeholder="Select NPI" /></SelectTrigger>
                    <SelectContent className="z-[200]">
                      {providers.map((p) => (
                        <SelectItem key={p.npi_number} value={p.npi_number}>
                          {p.provider_name} · {p.npi_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {configured ? (
                <div>
                  <Label className="text-[11px] text-[var(--gray2)]">Pick a time slot</Label>
                  <div className="mt-1">
                    <SlotGrid config={config} date={date}
                      onDateChange={(d) => { setDate(d); setSlotStart(null); setWhen(""); }}
                      appointments={appts} npi={npi} providerName={selectedProvider?.provider_name}
                      selectedStart={slotStart} onPick={pickSlot} />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] text-[var(--gray2)]">Date & time</Label>
                    <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1" data-testid="schedule-when" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-[var(--gray2)]">Duration (min)</Label>
                    <Input type="number" min="5" step="5" value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1" data-testid="schedule-duration" />
                  </div>
                </div>
              )}
              {npi && !configured ? (
                <div className="text-[11px] -mt-1.5" style={{ color: "#8A8F99" }} data-testid="schedule-no-hours-hint">
                  No working hours set for this provider — booking by free time entry. Add hours in Scheduler settings to book by open slots.
                </div>
              ) : null}
              <div>
                <Label className="text-[11px] text-[var(--gray2)]">Visit type</Label>
                {visitTypes.length > 0 ? (
                  <Select value={visitTypeId} onValueChange={setVisitTypeId}>
                    <SelectTrigger className="mt-1" data-testid="schedule-visit-type"><SelectValue placeholder="Select visit type" /></SelectTrigger>
                    <SelectContent className="z-[200]">
                      {visitTypes.map((v) => {
                        const id = String(v.visit_type_id ?? v.id);
                        return <SelectItem key={id} value={id}>{v.name ?? v.visit_type_name}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-[12px] text-[var(--gray2)] bg-[#F6F6F5] border border-black/[.08] rounded-md px-2.5 py-2 mt-1" data-testid="schedule-no-visit-types">
                    No visit types configured for Client {clientId}. Add them under the Note-to-Claim “Types of Visit” configurator and they’ll appear here.
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[11px] text-[var(--gray2)]">Reason</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1" data-testid="schedule-reason" />
              </div>
            </div>
          )}
        </div>

        {/* footer */}
        <div className="border-t border-black/[.08] px-[18px] py-3 flex items-center gap-2.5 bg-[#F6F6F5]">
          {result ? (
            <button onClick={() => onOpenChange(false)} data-testid="schedule-done"
              className="ml-auto btn-ink px-[18px] py-2.5 rounded-[9px] text-[13.5px] font-semibold">Done</button>
          ) : (
            <>
              <button onClick={() => onOpenChange(false)}
                className="bg-white border border-black/[.1] text-[#0F172A] rounded-[9px] px-[15px] py-2.5 text-[13.5px] hover:bg-black/[.03]">Cancel</button>
              <button onClick={submit} disabled={submitting || providers.length === 0} data-testid="schedule-submit"
                className="ml-auto btn-ink px-[18px] py-2.5 rounded-[9px] text-[13.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Booking…</> : "Book visit"}
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
